import { LitElement, html, css } from '@hh/global.js'
import { scrollToSmoothly } from './utils.js'

class ProgressCircle extends LitElement {
  static get properties() {
    return {
      strokeWidth: { type: Number },
      viewbox: { type: String },
      pathData: { type: String }
    };
  }

  constructor() {
    super();
    this.strokeWidth = 1.5;
    this.boundedUpdateProgress = this.updateProgress.bind(this);
    this.boundHandleScroll = this.handleScroll.bind(this);
  }

  firstUpdated() {
    super.firstUpdated();

    this.style.setProperty("--stroke-width", `${this.strokeWidth}px`);
    this.progressPath = this.shadowRoot.querySelector("[data-progress-path]");
    this.updateProgress();
    window.addEventListener("scroll", this.boundedUpdateProgress);
    window.addEventListener("resize", this.boundHandleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("scroll", this.boundedUpdateProgress);
    window.removeEventListener("resize", this.boundHandleScroll);
  }

  updateProgress() {
    if (!this.progressPath) {
      return;
    }

    const pathLength = this.progressPath.getTotalLength();
    const transition = "stroke-dashoffset 0ms linear";
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    const progress = pathLength - (scrollPosition * pathLength) / scrollHeight;

    this.progressPath.style.strokeDasharray = `${pathLength} ${pathLength}`;
    this.progressPath.style.strokeDashoffset = pathLength;
    this.progressPath.style.transition = transition;
    this.progressPath.style.WebkitTransition = transition;
    this.progressPath.style.strokeDashoffset = progress;
  }

  updateSvg() {
    const size = this.clientWidth;
    const radius = (size - this.strokeWidth) / 2;
    this.viewBox = `0 0 ${size} ${size}`;

    this.pathData = `
      M ${size / 2}, ${this.strokeWidth / 2}
      a ${radius},${radius} 0 1,1 0,${radius * 2}
      a ${radius},${radius} 0 1,1 0,-${radius * 2}
    `;
  }

  handleClick(event) {
    event.preventDefault();
    scrollToSmoothly(0, 500);
  }

  handleScroll() {
    this.updateSvg();
    this.updateProgress();
  }

  render() {
    this.updateSvg();

    return html`
      <button
        class="progress-holder"
        @click="${this.handleClick}"
        aria-label="${this.dataset?.buttonText}"
      >
        <slot></slot>
        <svg
          class="progress-circle svg-content"
          width="100%"
          height="100%"
          viewBox="${this.viewBox}"
        >
          <path
            data-progress-path
            class="progress-circle__path"
            d="${this.pathData}"
          />
        </svg>
      </button>
    `;
  }

  static styles = css`
    .progress-holder,
    .progress-circle {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      cursor: pointer;
      background-color: transparent;
    }

    .progress-holder {
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 100%;
      appearance: none;
      border: none;
      background-color: transparent;
      box-shadow: inset 0px 0px 0px var(--stroke-width) #B8B8B8;
    }

    .progress-circle__path {
      stroke: rgba(0, 0, 0, 1);
      stroke-width: var(--stroke-width);
      box-sizing: border-box;
      -webkit-transition: all 200ms linear;
      transition: all 200ms linear;
      fill: transparent;
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
      transition: stroke-dashoffset 0.3s;
    }

    ::slotted(button) {
      width: 100%;
      height: 100%;
      max-width: 50%;
      max-height: 50%;
    }
  `;
}

if (typeof customElements.get('scroll-progress-circle') === 'undefined') {
  customElements.define('scroll-progress-circle', ProgressCircle);
}

